import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import { getSEOConfig, updateSEOTags } from '../../i18n/utils/seo';
import type { SupportedLanguage } from '../../i18n/utils/language';

export default function MySQLTimestamp() {
  const { t, i18n } = useTranslation(['programming', 'common']);
  const [copySuccess, setCopySuccess] = useState('');

  // Update SEO
  useEffect(() => {
    const currentLang = i18n.language as SupportedLanguage;
    const seoConfig = getSEOConfig('mysqlTimestamp', currentLang);
    updateSEOTags(seoConfig, currentLang);
    document.documentElement.lang = currentLang;
  }, [i18n.language]);

  // Copy to clipboard
  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(id);
      setTimeout(() => setCopySuccess(''), 2000);
    });
  };

  const CodeBlock = ({ code, language = 'sql', id }: { code: string; language?: string; id: string }) => (
    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
      <button
        onClick={() => copyToClipboard(code, id)}
        className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
      >
        {copySuccess === id ? t('common:button.copied') : t('common:button.copy')}
      </button>
      <pre className="text-green-400 text-sm font-mono whitespace-pre overflow-x-auto">
        {code}
      </pre>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-5xl mx-auto px-4 py-4 md:py-8">
        {/* Page Header */}
        <div className="text-center mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            {t('mysql.mainTitleSEO')}
          </h1>
          <p className="text-lg text-gray-600">{t('mysql.subtitle')}</p>
        </div>

        {/* Introduction */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <p className="text-gray-700 leading-relaxed">{t('mysql.intro')}</p>
        </div>

        {/* UNIX_TIMESTAMP() Function */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('mysql.unixTimestampTitle')}</h2>
          <p className="text-gray-600 mb-6">{t('mysql.unixTimestampDesc')}</p>

          <div className="space-y-6">
            {/* Example 1: Get Current Timestamp */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">1</span>
                {t('mysql.getCurrentTimestamp')}
              </h3>
              <CodeBlock
                id="mysql-1"
                code={`SELECT UNIX_TIMESTAMP();
-- Output: 1735948800 (current Unix timestamp in seconds)

-- Get milliseconds
SELECT UNIX_TIMESTAMP() * 1000;
-- Output: 1735948800000`}
              />
              <p className="mt-2 text-sm text-gray-600">💡 {t('mysql.example1')}</p>
            </div>

            {/* Example 2: Convert Date to Timestamp */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">2</span>
                {t('mysql.convertDateToTimestamp')}
              </h3>
              <CodeBlock
                id="mysql-2"
                code={`SELECT UNIX_TIMESTAMP('2024-12-19 17:39:44');
-- Output: 1734630584

-- Convert specific column
SELECT id, UNIX_TIMESTAMP(created_at) AS timestamp
FROM users
WHERE id = 1;`}
              />
              <p className="mt-2 text-sm text-gray-600">💡 {t('mysql.example2')}</p>
            </div>
          </div>
        </div>

        {/* FROM_UNIXTIME() Function */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('mysql.fromUnixtimeTitle')}</h2>
          <p className="text-gray-600 mb-6">{t('mysql.fromUnixtimeDesc')}</p>

          <div className="space-y-6">
            {/* Example 1: Convert Timestamp to Date */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">1</span>
                {t('mysql.convertTimestampToDate')}
              </h3>
              <CodeBlock
                id="mysql-3"
                code={`SELECT FROM_UNIXTIME(1734630584);
-- Output: 2024-12-19 17:39:44

-- From milliseconds
SELECT FROM_UNIXTIME(1734630584000 / 1000);
-- Output: 2024-12-19 17:39:44`}
              />
            </div>

            {/* Example 2: Format with Pattern */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">2</span>
                {t('mysql.formatTimestamp')}
              </h3>
              <CodeBlock
                id="mysql-4"
                code={`SELECT FROM_UNIXTIME(1734630584, '%Y-%m-%d %H:%i:%s');
-- Output: 2024-12-19 17:39:44

SELECT FROM_UNIXTIME(1734630584, '%M %d, %Y at %h:%i %p');
-- Output: December 19, 2024 at 05:39 PM

SELECT FROM_UNIXTIME(1734630584, '%W, %M %d, %Y');
-- Output: Thursday, December 19, 2024`}
              />
            </div>
          </div>
        </div>

        {/* TIMESTAMP vs DATETIME */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('mysql.typesTitle')}</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* TIMESTAMP Type */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200">
              <h3 className="text-xl font-bold text-blue-900 mb-4">{t('mysql.timestampType')}</h3>
              <ul className="space-y-3">
                {[1, 2, 3, 4].map((num) => (
                  <li key={num} className="flex items-start">
                    <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">{t(`mysql.timestampFeature${num}`)}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* DATETIME Type */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border-2 border-purple-200">
              <h3 className="text-xl font-bold text-purple-900 mb-4">{t('mysql.datetimeType')}</h3>
              <ul className="space-y-3">
                {[1, 2, 3, 4].map((num) => (
                  <li key={num} className="flex items-start">
                    <svg className="w-5 h-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700">{t(`mysql.datetimeFeature${num}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Practical Examples */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('mysql.practicalTitle')}</h2>

          <div className="space-y-6">
            {/* Insert */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('mysql.example.insert')}</h3>
              <CodeBlock
                id="mysql-5"
                code={`-- Create table with auto-timestamp columns
CREATE TABLE posts (
  id INT AUTO_INCREMENT PRIMARY KEY,
  title VARCHAR(255),
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
  updated_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP
);

-- Insert with current timestamp
INSERT INTO posts (title) VALUES ('My First Post');

-- Insert with specific timestamp
INSERT INTO posts (title, created_at)
VALUES ('Historical Post', FROM_UNIXTIME(1609459200));`}
              />
            </div>

            {/* Query */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('mysql.example.query')}</h3>
              <CodeBlock
                id="mysql-6"
                code={`-- Get posts from last 7 days
SELECT * FROM posts
WHERE created_at >= UNIX_TIMESTAMP(DATE_SUB(NOW(), INTERVAL 7 DAY));

-- Get posts between two timestamps
SELECT * FROM posts
WHERE UNIX_TIMESTAMP(created_at) BETWEEN 1734000000 AND 1735000000;

-- Count posts by day
SELECT
  FROM_UNIXTIME(UNIX_TIMESTAMP(created_at), '%Y-%m-%d') AS date,
  COUNT(*) AS post_count
FROM posts
GROUP BY date
ORDER BY date DESC;`}
              />
            </div>

            {/* Aggregation */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('mysql.example.aggregation')}</h3>
              <CodeBlock
                id="mysql-7"
                code={`-- Group by hour
SELECT
  FROM_UNIXTIME(UNIX_TIMESTAMP(created_at), '%Y-%m-%d %H:00:00') AS hour,
  COUNT(*) AS count
FROM posts
GROUP BY hour;

-- Get posts per month
SELECT
  FROM_UNIXTIME(UNIX_TIMESTAMP(created_at), '%Y-%m') AS month,
  COUNT(*) AS posts_per_month
FROM posts
GROUP BY month
ORDER BY month DESC;`}
              />
            </div>
          </div>
        </div>

        {/* Timezone Handling */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('mysql.timezoneTitle')}</h2>
          <p className="text-gray-600 mb-6">{t('mysql.timezoneDesc')}</p>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('mysql.setTimezone')}</h3>
              <CodeBlock
                id="mysql-8"
                code={`-- Set timezone for current session
SET time_zone = '+08:00';  -- Beijing Time
SET time_zone = 'UTC';     -- UTC
SET time_zone = 'America/New_York';  -- Eastern Time

-- Check current timezone
SELECT @@session.time_zone;`}
              />
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('mysql.convertTimezone')}</h3>
              <CodeBlock
                id="mysql-9"
                code={`-- Convert timestamp to different timezone
SELECT
  created_at,
  CONVERT_TZ(created_at, 'UTC', 'America/New_York') AS ny_time,
  CONVERT_TZ(created_at, 'UTC', 'Asia/Tokyo') AS tokyo_time
FROM posts;`}
              />
            </div>
          </div>
        </div>

        {/* Common Use Cases */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('mysql.useCasesTitle')}</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((num) => (
              <div key={num} className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-bold text-gray-800 mb-2">{t(`mysql.useCase${num}.title`)}</h3>
                <p className="text-sm text-gray-600">{t(`mysql.useCase${num}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* TIMESTAMPDIFF Function */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">MySQL TIMESTAMPDIFF() - Calculate Time Differences</h2>
          <p className="text-gray-600 mb-6">
            TIMESTAMPDIFF() calculates the difference between two timestamps and returns the result in the specified unit (seconds, minutes, hours, days, etc.).
          </p>

          <div className="space-y-6">
            {/* Basic Syntax */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">Syntax</span>
                TIMESTAMPDIFF(unit, start_datetime, end_datetime)
              </h3>
              <CodeBlock
                id="timestampdiff-1"
                code={`-- Calculate difference in seconds
SELECT TIMESTAMPDIFF(SECOND, '2024-01-01 10:00:00', '2024-01-01 10:05:30');
-- Output: 330 seconds

-- Calculate difference in minutes
SELECT TIMESTAMPDIFF(MINUTE, '2024-01-01 10:00:00', '2024-01-01 12:30:00');
-- Output: 150 minutes

-- Calculate difference in hours
SELECT TIMESTAMPDIFF(HOUR, '2024-01-01 00:00:00', '2024-01-02 00:00:00');
-- Output: 24 hours

-- Calculate difference in days
SELECT TIMESTAMPDIFF(DAY, '2024-01-01', '2024-01-15');
-- Output: 14 days

-- Calculate difference in months
SELECT TIMESTAMPDIFF(MONTH, '2024-01-01', '2024-06-01');
-- Output: 5 months

-- Calculate difference in years
SELECT TIMESTAMPDIFF(YEAR, '2020-01-01', '2024-01-01');
-- Output: 4 years`}
              />
            </div>

            {/* Practical Examples */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">Examples</span>
                Real-World Use Cases
              </h3>
              <CodeBlock
                id="timestampdiff-2"
                code={`-- Calculate user age in years
SELECT
  username,
  TIMESTAMPDIFF(YEAR, birth_date, CURDATE()) AS age
FROM users;

-- Find posts created in the last 24 hours
SELECT
  title,
  TIMESTAMPDIFF(HOUR, created_at, NOW()) AS hours_ago
FROM posts
WHERE TIMESTAMPDIFF(HOUR, created_at, NOW()) < 24;

-- Calculate session duration in minutes
SELECT
  user_id,
  TIMESTAMPDIFF(MINUTE, login_time, logout_time) AS session_duration
FROM user_sessions
WHERE logout_time IS NOT NULL;

-- Find overdue tasks (days past deadline)
SELECT
  task_name,
  TIMESTAMPDIFF(DAY, deadline, NOW()) AS days_overdue
FROM tasks
WHERE deadline < NOW();

-- Calculate average response time in seconds
SELECT
  AVG(TIMESTAMPDIFF(SECOND, request_time, response_time)) AS avg_response_seconds
FROM support_tickets;`}
              />
            </div>

            {/* With UNIX_TIMESTAMP */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded text-sm mr-3">Tip</span>
                Using TIMESTAMPDIFF with Unix Timestamps
              </h3>
              <CodeBlock
                id="timestampdiff-3"
                code={`-- Convert Unix timestamps to datetime first
SELECT TIMESTAMPDIFF(
  HOUR,
  FROM_UNIXTIME(1704096000),
  FROM_UNIXTIME(1704182400)
) AS hours_diff;
-- Output: 24 hours

-- Or calculate directly in seconds
SELECT (1704182400 - 1704096000) AS seconds_diff;
-- Output: 86400 seconds (then divide by 3600 for hours)`}
              />
            </div>
          </div>
        </div>

        {/* Extract Year, Month, Day from Timestamp */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Extract Year, Month, Day from Timestamp in MySQL</h2>
          <p className="text-gray-600 mb-6">
            Learn how to extract specific date parts (year, month, day, hour, etc.) from Unix timestamps or datetime columns in MySQL.
          </p>

          <div className="space-y-6">
            {/* From DATETIME column */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">1</span>
                Extract from DATETIME Column
              </h3>
              <CodeBlock
                id="extract-1"
                code={`-- Extract year, month, day
SELECT
  created_at,
  YEAR(created_at) AS year,
  MONTH(created_at) AS month,
  DAY(created_at) AS day,
  DAYNAME(created_at) AS day_name,
  MONTHNAME(created_at) AS month_name
FROM posts;

-- Extract time parts
SELECT
  created_at,
  HOUR(created_at) AS hour,
  MINUTE(created_at) AS minute,
  SECOND(created_at) AS second
FROM posts;

-- Extract week information
SELECT
  created_at,
  WEEK(created_at) AS week_number,
  WEEKDAY(created_at) AS weekday,  -- 0=Monday, 6=Sunday
  DAYOFWEEK(created_at) AS day_of_week,  -- 1=Sunday, 7=Saturday
  DAYOFYEAR(created_at) AS day_of_year
FROM posts;`}
              />
            </div>

            {/* From Unix Timestamp */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">2</span>
                Extract from Unix Timestamp
              </h3>
              <CodeBlock
                id="extract-2"
                code={`-- Convert Unix timestamp to datetime first, then extract
SELECT
  YEAR(FROM_UNIXTIME(1704096000)) AS year,
  MONTH(FROM_UNIXTIME(1704096000)) AS month,
  DAY(FROM_UNIXTIME(1704096000)) AS day;
-- Output: 2024, 1, 1

-- Extract from timestamp column
SELECT
  YEAR(FROM_UNIXTIME(timestamp_column)) AS year,
  MONTH(FROM_UNIXTIME(timestamp_column)) AS month,
  DAY(FROM_UNIXTIME(timestamp_column)) AS day
FROM events;`}
              />
            </div>

            {/* Practical Queries */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded text-sm mr-3">3</span>
                Practical Queries with Date Extraction
              </h3>
              <CodeBlock
                id="extract-3"
                code={`-- Group by year and month
SELECT
  YEAR(created_at) AS year,
  MONTH(created_at) AS month,
  COUNT(*) AS post_count
FROM posts
GROUP BY year, month
ORDER BY year DESC, month DESC;

-- Find all posts from 2024
SELECT * FROM posts
WHERE YEAR(created_at) = 2024;

-- Find posts from January
SELECT * FROM posts
WHERE MONTH(created_at) = 1;

-- Get posts from specific date (ignoring time)
SELECT * FROM posts
WHERE DATE(created_at) = '2024-01-01';

-- Count posts by day of week
SELECT
  DAYNAME(created_at) AS day,
  COUNT(*) AS count
FROM posts
GROUP BY DAYNAME(created_at), DAYOFWEEK(created_at)
ORDER BY DAYOFWEEK(created_at);`}
              />
            </div>
          </div>
        </div>

        {/* Compare Timestamps */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Compare Timestamps in MySQL</h2>
          <p className="text-gray-600 mb-6">
            Learn different ways to compare timestamps in MySQL for filtering, sorting, and conditional logic.
          </p>

          <div className="space-y-6">
            {/* Basic Comparisons */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">1</span>
                Basic Timestamp Comparisons
              </h3>
              <CodeBlock
                id="compare-1"
                code={`-- Find records after a specific timestamp
SELECT * FROM events
WHERE created_at > '2024-01-01 00:00:00';

-- Find records before a timestamp
SELECT * FROM events
WHERE created_at < '2024-12-31 23:59:59';

-- Find records between two timestamps
SELECT * FROM events
WHERE created_at BETWEEN '2024-01-01' AND '2024-12-31';

-- Compare two columns
SELECT * FROM sessions
WHERE logout_time > login_time;

-- Find records where one timestamp is NULL
SELECT * FROM sessions
WHERE logout_time IS NULL;  -- Still logged in

-- Compare with current time
SELECT * FROM events
WHERE created_at > NOW();  -- Future events

SELECT * FROM tasks
WHERE deadline < NOW();  -- Overdue tasks`}
              />
            </div>

            {/* Unix Timestamp Comparisons */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">2</span>
                Compare Unix Timestamps
              </h3>
              <CodeBlock
                id="compare-2"
                code={`-- Compare Unix timestamp (integer column)
SELECT * FROM events
WHERE unix_timestamp > 1704096000;

-- Convert datetime to timestamp for comparison
SELECT * FROM events
WHERE UNIX_TIMESTAMP(created_at) > 1704096000;

-- Compare two Unix timestamp columns
SELECT * FROM events
WHERE end_timestamp > start_timestamp;

-- Find events in last 24 hours (using Unix timestamp)
SELECT * FROM events
WHERE unix_timestamp > (UNIX_TIMESTAMP() - 86400);`}
              />
            </div>

            {/* Advanced Comparisons */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded text-sm mr-3">3</span>
                Advanced Comparison Patterns
              </h3>
              <CodeBlock
                id="compare-3"
                code={`-- Find earliest and latest timestamps
SELECT
  MIN(created_at) AS earliest,
  MAX(created_at) AS latest,
  TIMESTAMPDIFF(DAY, MIN(created_at), MAX(created_at)) AS days_span
FROM posts;

-- Compare and calculate differences
SELECT
  event_name,
  start_time,
  end_time,
  CASE
    WHEN end_time > start_time THEN 'Valid'
    ELSE 'Invalid'
  END AS status,
  TIMESTAMPDIFF(MINUTE, start_time, end_time) AS duration_minutes
FROM events;

-- Find overlapping time ranges
SELECT a.id AS event1, b.id AS event2
FROM events a
JOIN events b ON a.id < b.id
WHERE a.start_time < b.end_time
  AND a.end_time > b.start_time;

-- Find gaps between consecutive events
SELECT
  e1.end_time AS previous_end,
  e2.start_time AS next_start,
  TIMESTAMPDIFF(MINUTE, e1.end_time, e2.start_time) AS gap_minutes
FROM events e1
JOIN events e2 ON e2.id = e1.id + 1
WHERE e1.end_time < e2.start_time;`}
              />
            </div>
          </div>
        </div>

        {/* Performance Tips */}
        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8 border-2 border-yellow-200">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            ⚡ {t('mysql.performanceTitle')}
          </h2>
          <ul className="space-y-3">
            {[1, 2, 3, 4].map((num) => (
              <li key={num} className="flex items-start">
                <span className="text-yellow-600 mr-2">✓</span>
                <span className="text-gray-700">{t(`mysql.perf${num}`)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* FAQ */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            {t('common:faq', { defaultValue: 'FAQ' })}
          </h2>

          <div className="space-y-4">
            {[1, 2, 3, 4].map((num) => (
              <details key={num} className="group">
                <summary className="flex justify-between items-center cursor-pointer list-none p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
                  <span className="font-semibold text-gray-800">{t(`mysql.faq.q${num}`)}</span>
                  <span className="transition group-open:rotate-180">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </summary>
                <div className="px-4 py-3 text-gray-600">
                  {t(`mysql.faq.a${num}`)}
                </div>
              </details>
            ))}
          </div>
        </div>

        {/* Related Tools */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {t('common:relatedTools', { defaultValue: 'Related Tools' })}
          </h2>

          <div className="grid md:grid-cols-3 gap-4">
            <a href="/timestamp-converter" className="block p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors">
              <h3 className="font-semibold text-gray-800 mb-1">Unix Timestamp Converter</h3>
              <p className="text-sm text-gray-600">Convert timestamps online</p>
            </a>
            <a href="/sql-timestamp" className="block p-4 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 transition-colors">
              <h3 className="font-semibold text-gray-800 mb-1">SQL Timestamp (All Databases)</h3>
              <p className="text-sm text-gray-600">MySQL, PostgreSQL, Oracle, SQL Server</p>
            </a>
            <a href="/python-timestamp" className="block p-4 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors">
              <h3 className="font-semibold text-gray-800 mb-1">Python Timestamp Guide</h3>
              <p className="text-sm text-gray-600">datetime, time modules</p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
